import { makeStyles } from '@material-ui/core/styles';
import { ButtonProps } from '@material-ui/core/Button/Button';
import { Button } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { forwardRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { COLORS } from 'src/constants/Color';

const useStyles = (color: string, lightColor: string) => makeStyles(() => ({
  actionButtonDefault: {
    minWidth: '42px',
    padding: 0,
    backgroundColor: color,
  },
  actionButtonCategory: {
    minWidth: '42px',
    padding: 0,
    color: COLORS.white,
    backgroundColor: color,
    border: '1px solid',
    borderColor: color,
    '&:hover': {
      border: '1px solid',
      borderColor: color,
      backgroundColor: lightColor,
      color,
    },
  },
}));

const useMobileStyles = (color: string, lightColor: string) => makeStyles(() => ({
  actionButtonDefault: {
    minWidth: '30px',
    padding: 0,
    backgroundColor: color,
  },
  actionButtonCategory: {
    minWidth: '30px',
    padding: 0,
    color: COLORS.white,
    backgroundColor: color,
    border: '1px solid',
    borderColor: color,
    '&:focus': {
      color: COLORS.white,
      backgroundColor: lightColor,
      borderColor: color,
    },
  },
}));

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum MENU_BUTTON_CATEGORY {
  presets = 'presets',
  action = 'action',
  download = 'download',
  selection = 'selection',
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const MENU_BUTTON_CATEGORY_COLOR: Record<MENU_BUTTON_CATEGORY, string> = {
  [MENU_BUTTON_CATEGORY.presets]: COLORS.primary,
  [MENU_BUTTON_CATEGORY.action]: COLORS.secondary,
  [MENU_BUTTON_CATEGORY.download]: COLORS.download,
  [MENU_BUTTON_CATEGORY.selection]: COLORS.action,
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const MENU_BUTTON_CATEGORY_COLOR_LIGHT: Record<MENU_BUTTON_CATEGORY, string> = {
  [MENU_BUTTON_CATEGORY.presets]: COLORS.primaryLight,
  [MENU_BUTTON_CATEGORY.action]: COLORS.secondaryLight,
  [MENU_BUTTON_CATEGORY.download]: COLORS.downloadLight,
  [MENU_BUTTON_CATEGORY.selection]: COLORS.actionLight,
};

export type CustomProps = {
  category?: MENU_BUTTON_CATEGORY,
  sizeSmall?: boolean,
  loading?: boolean,
};

type Props = CustomProps & ButtonProps;

const ActionButton = (props: Props, ref: any) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 680px)' });
  const { category, children, disabled } = props;
  const { loading, ...buttonProps } = props;
  const color = category ? MENU_BUTTON_CATEGORY_COLOR[category] : COLORS.white;
  const lightColor = category ? MENU_BUTTON_CATEGORY_COLOR_LIGHT[category] : COLORS.white;
  const spinnerColor = category ? color : COLORS.darkgrey;
  const classes = isTabletOrMobile
    ? useMobileStyles(color, lightColor)()
    : useStyles(color, lightColor)();

  const { style } = props;

  const isDisabled = disabled || loading;

  const getStyle = () => {
    const localStyle = { ...style } as any;
    localStyle.opacity = disabled ? 0.4 : 1;
    if (isDisabled) {
      localStyle.color = COLORS.white;
      localStyle.backgroundColor = lightColor;
      localStyle.border = `1px solid ${category ? color : COLORS.darkgrey}`;
    }
    return localStyle;
  };

  return (
    <Button
      ref={ref}
      className={category ? classes.actionButtonCategory : classes.actionButtonDefault}
      style={getStyle()}
      {...buttonProps as ButtonProps}
      disabled={isDisabled}>
      <span
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <span style={{ display: 'inline-flex', opacity: loading ? 0.4 : 1 }}>
          {children}
        </span>
        {loading && (
          <CircularProgress
            size={16}
            color="inherit"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 'auto',
              color: spinnerColor,
            }} />
        )}
      </span>
    </Button>
  );
};

export default forwardRef(ActionButton);
